/**
 * NOTE: This file is largely ported from the old `src/app/auth.tsx` file.
 * Chained `thenable` promises are replaced with `async/await` syntax.
 * The rest of the logic should have remained unchanged, but in case of debugging,
 * please refer to the original file for `git blame` history.
 */
import { createContext, useContext, useState } from 'react';
import { getAppConfig } from './config';
import {
  captureMessage,
  setTag as setSentryTag,
  setUser as setSentryUser,
} from '@sentry/react';
import {
  DISTRIBUTION_CLIENT,
  ClientContextNotFoundError,
  HttpNotFoundError,
  HttpUnauthorizedError,
} from '@/features/types';
import { withForwardedParams } from '@/features/utils';
import TagManager from 'react-gtm-module';
import { isClientNameLoaded } from '@/views/map/utils';
import { METADATA_SESSION_KEY, NOT_USED } from './constants';
import { ClientContextErrorCode } from '@/generated/types/navigator/types.gen';
import type {
  AnalysisConfigOutput,
  ClientContextNotFoundDetail,
  ClientDetail,
  ClientSummary,
  MapConfig,
  UserDetails,
} from '@/generated/types/navigator/types.gen';
import { type IntercomProps } from 'react-use-intercom';

const INTERNAL_COMPANY = 'Overstory';
const INTERNAL_COMPANYID = '0';

export type UserSigninFn = (
  token: string,
  apiIdentity: string,
  email: string,
  /**
   * Suggested client code for impersonation. Applied only after the user is
   * confirmed `is_superuser`; ignored for everyone else so a leaked URL can
   * never form `internal@<CODE>` for an external user.
   */
  internalClientCodeHint?: string
) => Promise<void>;

export interface ClientContext {
  clientId: number;
  name: string;
  code: string;
  clientType: string;
  deliveries: Delivery[];
  viewState: MapConfig;
  analysis_config: AnalysisConfigOutput;
  releaseId: string | null;
}

interface Delivery {
  id: number;
  identifier: string;
  description: string;
}

export interface UserAndClientContextInterface {
  token: string;
  /**
   * Identity slug sent as `X-Overstory-Email` for backend client scoping.
   * Email-shaped, but not necessarily the user's real email — for internal
   * users it is `internal@<CLIENT_CODE>` (impersonation).
   */
  apiIdentity: string;
  isInternalUser: boolean;
  canClientSwitch: boolean;
  /**
   * Flips to `true` once the user details have been fetched from the backend.
   * Distinguishes "still loading" from "loaded and not internal"
   */
  isUserLoaded: boolean;
  email: string;
  actualLoginEmail: string;
  context: ClientContext;
  hydrateUserAndClientContext: UserSigninFn;
  signout: () => void;
  refreshClientMetadata: () => Promise<void>;
}

/**
 * Determines which identity should be used for sign‑in and then delegates to
 * the provided `signin` callback. An explicit `clientCode` is an impersonation
 * request, so it becomes `internal@{clientCode}`; the provider drops it again
 * for anyone the backend does not confirm as a superuser.
 */
export const signInWithClient = async (
  signin: UserSigninFn,
  apiIdentity: string,
  clientCode?: string,
  internalClientCodeHint?: string
) => {
  const signInIdentity = clientCode ? `internal@${clientCode}` : apiIdentity;
  await signin(NOT_USED, signInIdentity, apiIdentity, internalClientCodeHint);
};

export const userClientContext =
  createContext<UserAndClientContextInterface | null>(null);

export const useUserClientContext = () => {
  const context = useContext(userClientContext);
  if (context) {
    return context;
  }
  throw new Error('useUserClientContext must be used within a provider');
};

/**
 * Retrieves the cached client metadata from `sessionStorage` (if present) and
 * converts it into the application’s `ClientContext`.
 */
export const getDefaultContext = () => {
  const metadata = sessionStorage.getItem(METADATA_SESSION_KEY);
  const data = metadata ? JSON.parse(metadata) : null;
  return getClientContext(data);
};

const DEFAULT_ANALYSIS_CONFIG: AnalysisConfigOutput = {
  wildfireThreshold: 0,
  clear_span_config: null,
  metric: 'ft',
};

/**
 * Converts raw `ClientDetail` data returned by the backend into the slimmer
 * `ClientContext` shape used by the UI. Also updates the document title for
 * internal users.
 */
export const getClientContext = (data: ClientDetail | null): ClientContext => {
  if (!data) {
    return {
      clientId: -1,
      name: 'Loading...',
      code: '',
      viewState: {
        longitude: 0,
        latitude: 0,
        zoom: 0,
        bbox: [0, 0, 0, 0],
      },
      clientType: DISTRIBUTION_CLIENT,
      deliveries: [],
      analysis_config: DEFAULT_ANALYSIS_CONFIG,
      releaseId: null,
    };
  }

  return {
    clientId: data.id,
    name: data.name,
    code: data.code,
    clientType: data.type ?? DISTRIBUTION_CLIENT,
    deliveries: data.deliveries.map((d) => ({
      id: d.id,
      identifier: d.identifier,
      description: d.description,
    })),
    viewState: data.map_config,
    analysis_config: data.analysis_config ?? DEFAULT_ANALYSIS_CONFIG,
    // ClientDetail is cached raw in sessionStorage, so a stored entry can carry
    // no release_id at all.
    releaseId: data.release_id ?? null,
  };
};

/**
 * Lazily initializes Google Tag Manager only once per page‑load and pushes
 * the Overstory client name into the data‑layer for downstream GA reporting.
 */
const initGoogleAnalytics = (clientName: string, gtmId: string) => {
  if (!isClientNameLoaded()) {
    TagManager.dataLayer({
      dataLayer: { clientName },
    });
    TagManager.initialize({ gtmId });
  }
};

/**
 * Configures the Intercom widget with company metadata (internal vs external)
 */
const updateIntercomUser = (
  updateIntercom: (props?: Partial<IntercomProps>) => void,
  email: string,
  userDetail: UserDetails,
  clientId: number,
  clientName: string
) => {
  const companyId = userDetail.is_superuser
    ? INTERNAL_COMPANYID
    : clientId.toString();
  const name = userDetail.is_superuser ? INTERNAL_COMPANY : clientName;

  updateIntercom({
    intercomUserJwt: userDetail.intercom_user_jwt ?? undefined,
    userId: userDetail.intercom_userid ?? undefined,
    email,
    company: { companyId, name },
  });
};

export const fetchUser = async (headers: HeadersInit): Promise<UserDetails> => {
  const response = await fetch(`/v1/user/`, {
    headers,
    credentials: 'include',
  });
  if (response.status === 401) {
    const body = await response.json();
    throw new HttpUnauthorizedError(body.detail);
  }
  if (response.status >= 400) {
    throw new Error(`Error retrieving user: ${response.status}`);
  }
  return response.json();
};

/**
 * Matches the 404 detail that get_client_context (navigator/core/context.py) attaches
 * when the client/delivery/release in the URL can't be resolved — a bad link rather
 * than a generally missing resource.
 */
const isClientContextNotFound = (
  detail: unknown
): detail is ClientContextNotFoundDetail =>
  typeof detail === 'object' &&
  detail !== null &&
  'code' in detail &&
  detail.code === ClientContextErrorCode.CLIENT_CONTEXT_NOT_FOUND;

/**
 * Retrieves the current client’s metadata from `/v1/client/`.
 */
export const fetchClientMetadata = async (
  headers: Headers | HeadersInit
): Promise<ClientDetail> => {
  const response = await fetch(
    withForwardedParams(`/v1/client/?compute_datasets=false`),
    {
      headers,
      credentials: 'include',
    }
  );
  if (response.status === 401) {
    const body = await response.json();
    throw new HttpUnauthorizedError(body.detail);
  }
  if (response.status === 404) {
    const body = await response.json().catch(() => undefined);
    const detail: unknown = body?.detail;
    if (isClientContextNotFound(detail)) {
      throw new ClientContextNotFoundError(
        detail.message || 'Client context not found'
      );
    }
    throw new HttpNotFoundError(
      typeof detail === 'string' ? detail : 'No client metadata found'
    );
  }
  if (response.status >= 400) {
    throw new Error(`Error retrieving client metadata: ${response.status}`);
  }

  return response.json();
};

/**
 * Retrieves the list of all clients from `/v1/client/all`.
 * Backend gates this to superusers.
 */
export const fetchClients = async (
  headers: Headers | HeadersInit
): Promise<ClientSummary[]> => {
  const response = await fetch(`/v1/client/all`, {
    headers,
    credentials: 'include',
  });
  if (response.status === 401) {
    const body = await response.json();
    throw new HttpUnauthorizedError(body.detail);
  }
  if (response.status >= 400) {
    throw new Error(`Error retrieving client list: ${response.status}`);
  }
  return response.json();
};

/**
 * Normalizes the user’s login e‑mail. In non‑development environments emails
 * may be prefixed with a string (`"prefix:user@example.com"`).
 */
export const getLoginEmail = (
  email: string,
  environment: 'development' | 'staging' | 'production' | 'performance'
): string => {
  return environment === 'development'
    ? email
    : email.substring(email.indexOf(':') + 1);
};

/**
 * Orchestrates the full client‑metadata bootstrap: fetches metadata, updates
 * Sentry, Intercom, Google Tag Manager, and finally stores the
 * metadata in application state.
 */
export const loadClientMetadata = async (
  headers: HeadersInit,
  updateEmail: string,
  userDetail: UserDetails,
  environment: string,
  tagManagerId: string,
  updateIntercom: (props?: Partial<IntercomProps>) => void,
  setClientMetadata: (data: ClientDetail) => void
) => {
  const data = await fetchClientMetadata(headers);

  updateIntercomUser(
    updateIntercom,
    updateEmail,
    userDetail,
    data.id,
    data.name
  );

  if (
    ['development', 'staging'].includes(environment) ||
    (environment === 'production' && !userDetail.is_superuser)
  ) {
    initGoogleAnalytics(data.name, tagManagerId);
  }

  setSentryTag('client', data.code);
  setSentryUser({ email: updateEmail });

  captureMessage('Setting client metadata');
  setClientMetadata(data);

  return data;
};

/** Update Sentry context if the state has changed */
export const useSentryContextUpdate = (email: string, clientCode: string) => {
  const [lastEmail, setLastEmail] = useState('');
  const [lastClientCode, setLastClientCode] = useState('');
  const { environment } = getAppConfig();
  const loginEmail = getLoginEmail(email, environment);

  if (email !== lastEmail || clientCode !== lastClientCode) {
    setLastEmail(email);
    setLastClientCode(clientCode);
    setSentryTag('client', clientCode);
    setSentryUser({ email: loginEmail });
  }
};
